import { Stack, Text, Tooltip } from '@sixfold/common-ui';
import { scaleLinear } from 'd3-scale';
import React from 'react';

import { Breakdown, SeriesPoint } from '../types';

import styles from './mobile.module.css';

const MAX_FILL_WITH_COUNT = 85;

export const BarList: React.FC<{
  items: Breakdown[];
  showShare?: boolean;
  renderLabel?: (item: Breakdown) => React.ReactNode;
  formatValue?: (item: Breakdown) => string;
  formatCount?: (item: Breakdown) => string;
  max?: number;
}> = ({ items, showShare = true, renderLabel, formatValue, formatCount, max }) => {
  const total = items.reduce((acc, { value }) => acc + value, 0);
  const scale = scaleLinear()
    .domain([0, max ?? Math.max(1, ...items.map(({ value }) => value))])
    .range([0, formatCount === undefined ? 100 : MAX_FILL_WITH_COUNT]);
  return (
    <Stack direction="column" wrap={false} gap="small">
      {items.map((item) => (
        <div key={item.id ?? item.label} className={styles.barRow}>
          <Text size="small">{renderLabel !== undefined ? renderLabel(item) : item.label}</Text>
          <div className={styles.barArea}>
            <div className={styles.barTrack}>
              <div className={styles.barFill} style={{ width: `${scale(item.value)}%` }} />
            </div>
            {formatCount !== undefined && (
              <Text size="small" className={styles.barCount} style={{ insetInlineStart: `${scale(item.value)}%` }}>
                {formatCount(item)}
              </Text>
            )}
          </div>
          <Text size="small" className={styles.barValue}>
            {formatValue !== undefined
              ? formatValue(item)
              : showShare && total > 0
                ? `${Math.round((item.value / total) * 100)}%`
                : item.value}
          </Text>
        </div>
      ))}
    </Stack>
  );
};

const CHART_HEIGHT = 140;
const LABEL_EVERY = 3;

export const ColumnChart: React.FC<{
  points: SeriesPoint[];
  formatValue: (point: SeriesPoint) => string;
}> = ({ points, formatValue }) => {
  const max = Math.max(1, ...points.map(({ value }) => value));
  const scale = scaleLinear().domain([0, max]).range([0, CHART_HEIGHT]);
  const gridTemplateColumns = `repeat(${points.length}, minmax(0, 1fr))`;
  return (
    <div>
      <div className={styles.columnChart} style={{ gridTemplateColumns, height: CHART_HEIGHT }}>
        {points.map((point) => (
          <Tooltip key={point.label} content={`${point.label}: ${formatValue(point)}`}>
            <div className={styles.columnBar} style={{ height: Math.max(2, scale(point.value)) }} />
          </Tooltip>
        ))}
      </div>
      <div className={styles.columnLabels} style={{ gridTemplateColumns }}>
        {points.map((point, index) => (
          <span key={point.label} className={styles.columnLabel}>
            {index % LABEL_EVERY === 0 || index === points.length - 1 ? point.label : ''}
          </span>
        ))}
      </div>
    </div>
  );
};
